
import { BaseService } from '../../../http/BaseHttpService';
import {
    ChatSessionLoadResult,
    ChatSessionsLoadResult,
    DeleteChatSessionsRequest,
    IChatSessionsService,
} from '../../../chat/interfaces/IChatSessionsService';
import { AssistantChatSessionDto } from '../dtos/responses/AssistantChatSessionDto';
import { AssistantMessageResponseDto } from '../dtos/responses/AssistantChatMessageDto';
import { mapAssistantChatSessionDtoToModel } from '../mappers/AssistantChatSessionMapper';
import { buildAssistantsUrl } from '../../../../features/utils/buildAssistantsUrl';
import { ChatProviderType } from '../../../../domain/chat/models/ChatProviderType';
import { AuthorRoles, ChatMessageType } from '../../../../domain/messages/models/ChatMessage';
import { DownloadChatSession } from '../../progpt/dtos/responses/ProGPTDownloadChatSessionDto';

interface AssistantsDownloadResponseDto {
    chatTitle: string;
    chatHistory: AssistantMessageResponseDto[];
}

export class AssistantsChatSessionsService extends BaseService implements IChatSessionsService {
    public readonly providerType = ChatProviderType.Assistants;
    private readonly subscriptionKey = process.env.REACT_APP_NEXUS_APIM_SUBSCRIPTION_KEY ?? '';
    private readonly subKeyHeader = process.env.REACT_APP_NEXUS_APIM_KEY_HEADER_NAME ?? '';

    constructor(private readonly baseUrl: string) {
        super();
    }


    public async getAllChatsAsync(accessToken: string): Promise<ChatSessionsLoadResult> {


        const response = await this.getResponseAsync<AssistantChatSessionDto[]>(
            {
                url: buildAssistantsUrl(this.baseUrl, 'chatSession/getAllChats'),
                method: 'GET',
                headers: {
                    [this.subKeyHeader]: this.subscriptionKey,
                },
            },

                accessToken,
            );

        return {
            sessions: response.map(mapAssistantChatSessionDtoToModel),
            documentsBySessionId: {},
        };
    }


    public async getChatAsync(chatId: string, accessToken: string): Promise<ChatSessionLoadResult> {


        const response = await this.getResponseAsync<AssistantChatSessionDto>(
            {
                url: buildAssistantsUrl(this.baseUrl, `chatSession/getChat/${chatId}`),
                method: 'GET',
                headers: {
                    [this.subKeyHeader]: this.subscriptionKey,
                },
            },

            accessToken,
        );

        return {
            session: mapAssistantChatSessionDtoToModel(response),
            documents: [],
        };
    }


    public async downloadChatSessionAsync(chatId: string, accessToken: string): Promise<DownloadChatSession> {
        const response = await this.getResponseAsync<AssistantsDownloadResponseDto>(
            {
                url: buildAssistantsUrl(this.baseUrl, `chatSession/${chatId}/download`),
                method: 'GET',
                headers: {
                    [this.subKeyHeader]: this.subscriptionKey,
                },
            },
            accessToken,
        );

        return {
            chatTitle: response.chatTitle,
            chatHistory: response.chatHistory.map((msg) => ({
                id: msg.id ?? '',
                chatId: msg.chatId,
                type: msg.type ?? ChatMessageType.Message,
                timestamp: msg.timestamp,
                userName: msg.userName,
                content: msg.content,
                authorRole: msg.authorRole === 1 ? AuthorRoles.Bot : AuthorRoles.User,
            })),
        };
    }

    public async deleteChatAsync(request: DeleteChatSessionsRequest, accessToken: string): Promise<void> {
        if (!request.chatId) {
            return;
        }

        await this.getResponseAsync(
            {
                url: buildAssistantsUrl(this.baseUrl, `chatSession`),
                method: 'DELETE',
                body: [request.chatId],
                headers: {
                    [this.subKeyHeader]: this.subscriptionKey,
                },
            },

            accessToken,
        );
    }
}
